import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  Plus,
  LinkSimple,
  CheckCircle,
  Clock,
  XCircle,
  PaperPlaneTilt,
  Copy,
  CurrencyDollar,
  MagnifyingGlass,
  Trash
} from '@phosphor-icons/react';


function StatusBadge({ status }) {
  const config = {
    'paid': { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Pagado' },
    'pending': { icon: Clock, color: 'text-yellow-600 bg-yellow-50', label: 'Pendiente' },
    'active': { icon: LinkSimple, color: 'text-blue-600 bg-blue-50', label: 'Activo' },
    'cancelled': { icon: XCircle, color: 'text-red-600 bg-red-50', label: 'Cancelado' },
  };
  const { icon: Icon, color, label } = config[status] || config.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`} data-testid={`link-status-${status}`}>
      <Icon size={14} weight="fill" />
      {label}
    </span>
  );
}

export default function PaymentLinksPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState({});
  const [form, setForm] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    description: '',
    amount: '',
    notes: ''
  });
  const [creating, setCreating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchLinks();
    fetchClients();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/payment-links`, { withCredentials: true });
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching payment links:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm({
        ...form,
        client_id: client.id,
        client_name: client.name,
        client_email: client.email
      });
    } else {
      setForm({ ...form, client_id: '' });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.amount || !form.description) {
      alert('Completa todos los campos requeridos');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/payment-links`, {
        ...form,
        amount: parseFloat(form.amount)
      }, { withCredentials: true });
      setShowCreate(false);
      setForm({ client_id: '', client_name: '', client_email: '', description: '', amount: '', notes: '' });
      fetchLinks();
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert('Error al crear el link de pago');
    } finally {
      setCreating(false);
    }
  };

  const handleSendEmail = async (linkId) => {
    setSending(prev => ({ ...prev, [linkId]: true }));
    try {
      await axios.post(`${API_URL}/api/payment-links/${linkId}/resend`, {}, { withCredentials: true });
      alert('Link de pago enviado por email');
      fetchLinks();
    } catch (error) {
      console.error('Error sending link:', error);
      alert('Error al enviar el email');
    } finally {
      setSending(prev => ({ ...prev, [linkId]: false }));
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    alert('Link copiado al portapapeles');
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredLinks.length && filteredLinks.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLinks.map(l => l.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} link(s) de pago seleccionado(s)? Esta accion no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${API_URL}/api/bulk/delete-payment-links`, { ids: selectedIds }, { withCredentials: true });
      alert(res.data?.message || 'Links eliminados');
      setSelectedIds([]);
      fetchLinks();
    } catch (error) {
      console.error('Error deleting payment links:', error);
      alert(error?.response?.data?.detail || 'Error al eliminar los links de pago');
    } finally {
      setDeleting(false);
    }
  };

  const filteredLinks = links.filter(l =>
    l.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payment-links-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Links de Pago</h1>
          <p className="text-slate-500 mt-1">Crea y envia links de pago independientes</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          data-testid="create-payment-link-btn"
        >
          <Plus size={18} weight="bold" />
          Nuevo Link de Pago
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Total Creados</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-links">{links.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Pagados</p>
          <p className="text-2xl font-bold text-green-600 mt-1" data-testid="paid-links">
            ${links.filter(l => l.payment_status === 'paid').reduce((sum, l) => sum + l.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1" data-testid="pending-links">
            ${links.filter(l => l.payment_status !== 'paid' && l.status !== 'cancelled').reduce((sum, l) => sum + l.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por cliente o descripcion..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          data-testid="search-payment-links"
        />
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-red-900">
            {selectedIds.length} link(s) seleccionado(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
              data-testid="clear-selection-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              {deleting ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} weight="bold" />}
              {deleting ? 'Eliminando...' : `Eliminar ${selectedIds.length}`}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <CircleNotch size={40} className="animate-spin text-blue-600" />
        </div>
      ) : filteredLinks.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <LinkSimple size={48} className="text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900">No hay links de pago</h3>
          <p className="text-slate-500 mt-1">Crea tu primer link de pago independiente</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payment-links-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === filteredLinks.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Descripcion</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Monto</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinks.map((link) => (
                  <tr key={link.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedIds.includes(link.id) ? 'bg-blue-50/40' : ''}`} data-testid={`payment-link-row-${link.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(link.id)}
                        onChange={() => toggleSelectOne(link.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        data-testid={`select-link-${link.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{link.client_name}</p>
                      <p className="text-xs text-slate-500">{link.client_email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">{link.description}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">${link.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={link.payment_status === 'paid' ? 'paid' : link.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(link.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {link.checkout_url && link.payment_status !== 'paid' && (
                          <>
                            <button
                              onClick={() => handleCopyLink(link.checkout_url)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Copiar Link"
                              data-testid={`copy-link-${link.id}`}
                            >
                              <Copy size={16} />
                            </button>
                            <button
                              onClick={() => handleSendEmail(link.id)}
                              disabled={sending[link.id]}
                              className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Enviar por Email"
                              data-testid={`send-link-${link.id}`}
                            >
                              {sending[link.id] ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="create-payment-link-modal">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <CurrencyDollar size={24} className="text-blue-600" />
                Nuevo Link de Pago
              </h2>
              <p className="text-sm text-slate-500 mt-1">Crea un link de pago independiente sin cotizacion</p>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cliente (opcional)</label>
                <select
                  value={form.client_id}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="select-client"
                >
                  <option value="">-- Seleccionar cliente o escribir manual --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="input-client-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="input-client-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripcion / Concepto *</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ej: Anticipo instalacion camaras, Servicio de mantenimiento..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="input-description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Monto (USD) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="input-amount"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas (opcional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="input-notes"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                  data-testid="cancel-create-btn"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                  data-testid="submit-payment-link-btn"
                >
                  {creating ? <CircleNotch size={18} className="animate-spin" /> : <LinkSimple size={18} />}
                  {creating ? 'Creando...' : 'Crear Link de Pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
